/**
 *  Sagas for the ResourceNames resource managment using Swagger client
 *
 * Each saga watcher intercepts a trigger action, does the asyncrhonous work in the respective worker saga and dispatches a success or a failure action.
 * Fetch calls are made via the swagger tags interface requests
 *
 */

import { apiClient, applyHeaders, buildHeaders } from "@bkry/bowline-utils";
import { ethers } from "ethers";
import { call, put, takeEvery } from "redux-saga/effects";
import { storeIncluded } from "../dictionarySagas";
import { actions } from "./index";
import generateNonce from "./utils/generateNonce";
/** Show Saga
 *  @description: connects to the showResourceName operation
 *  @param {number} action.payload the ResourceName id
 */
export function* show(action) {
  const account = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.Creators.showCreator,
      {
        address: account,
      },
      {
        requestInterceptor: applyHeaders(headers),
      }
    );
    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.showSuccess(payload.obj.data));

    // If requester is current account, also fire siwe success
    if (account === headers["uid"])
      yield put(actions.siweSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.showFail({ message: e.message, ...e }));
  }
}

export function* siwe(action) {
  let headers = yield buildHeaders();
  let { account, nonce, nonceIssuedAt, callbackSuccess, provider } = action.payload;
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.Creators.creatorSIWE,
      {
        address: account,
      },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody: {
          nonce: nonce,
          nonce_issued_at: nonceIssuedAt,
          provider
        },
      }
    );

    if (payload.obj?.included)
      yield* storeIncluded({ payload: payload.obj.included });
    yield put(actions.siweSuccess(payload.obj.data));
    if (callbackSuccess) callbackSuccess();
  } catch (e) {
    yield put(actions.siweFail({ message: e.message, ...e }));
  }
}

export function* prepareSiwe(action) {
  let { account, callbackSuccess } = action.payload;
  try {
    const nonce = generateNonce(8);
    const nonceIssuedAt = new Date().toUTCString();
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    const signature = yield signer.signMessage(
      `bowline.app wants you to sign in with your Ethereum account:
${account}

Thanks for using bowline.app! Sign this message to login. No password needed!

I hereby accept the Terms of Service: https://bowline.app/tos

URI: https://bowline.app
Version: 1
Nonce: ${nonce}
Issued At: ${nonceIssuedAt}
`
    );

    yield put(actions.prepareSiweSuccess({ signature }));
    callbackSuccess({
      nonce: nonce,
      nonceIssuedAt: nonceIssuedAt,
    });
  } catch (e) {
    yield put(actions.prepareSiweFail({ message: e.message, ...e }));
  }
}

export function* getDeployments(action) {
  const { chain } = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.Creators.getCreatorDeployments,
      {
        chain,
      },
      {
        requestInterceptor: applyHeaders(headers),
      }
    );
    yield put(actions.getDeploymentsSuccess(payload.obj.txs));
  } catch (e) {
    yield put(actions.getDeploymentsFail({ message: e.message, ...e }));
  }
}

export function* update(action) {
  const { account, emailUnconfirmed, callbackSuccess } = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    let payload = yield call(
      SwaggerClient.apis.Creators.updateCreator,
      {
        address: account,
      },
      {
        requestInterceptor: applyHeaders(headers),
        requestBody: {
          email_unconfirmed: emailUnconfirmed,
        },
      }
    );

    yield put(actions.updateSuccess(payload.obj.data));
    if (callbackSuccess) callbackSuccess(emailUnconfirmed);
  } catch (e) {
    yield put(actions.updateFail({ message: e.message, ...e }));
  }
}

export function* sendConfirmation(action) {
  const { account, isResend } = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.Creators.sendConfirmCreator,
      {
        address: account,
        is_resend: isResend,
      },
      {
        requestInterceptor: applyHeaders(headers),
      }
    );

    yield put(actions.sendConfirmationSuccess(payload.obj.data));
  } catch (e) {}
}

export function* confirm(action) {
  const { account, email, confirmationToken } = action.payload;
  let headers = yield buildHeaders();
  try {
    const SwaggerClient = yield call(apiClient);
    const payload = yield call(
      SwaggerClient.apis.Creators.confirmCreator,
      {
        address: account,
        email,
        confirmation_token: confirmationToken,
      },
      {
        requestInterceptor: applyHeaders(headers),
      }
    );

    yield put(actions.confirmSuccess(payload.obj.data));
  } catch (e) {
    yield put(actions.confirmFail({ message: e.message, ...e }));
  }
}

/**
 * Saga Watchers
 * The exported list of sagas registered. When one of the action types is dispatched
 * the related worker saga is invoked.
 * Each saga is executed in a different thread
 */
function* creatorsSaga() {
  yield takeEvery(actions.prepareSiwe, prepareSiwe);
  yield takeEvery(actions.siwe, siwe);
  yield takeEvery(actions.show, show);
  yield takeEvery(actions.getDeployments, getDeployments);
  yield takeEvery(actions.update, update);
  yield takeEvery(actions.sendConfirmation, sendConfirmation);
  yield takeEvery(actions.confirm, confirm);
}
export default creatorsSaga;
